import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function Billing() {
  const [wallet, setWallet] = useState(null);
  const [walletTxs, setWalletTxs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [talent, setTalent] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [customCredits, setCustomCredits] = useState(1);
  const creditPrice = packages[0] ? packages[0].price / packages[0].credits : 5000;

  const load = () => {
    api.get("/billing/wallet").then(({ data }) => {
      setWallet(data.wallet);
      setWalletTxs(data.transactions);
      setPackages(data.packages);
    }).catch(() => {});
    api.get("/billing/invoices").then(({ data }) => setInvoices(data.invoices)).catch(() => {});
    api.get("/billing/talent").then(({ data }) => setTalent(data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const buy = async (pkg) => {
    setBusyId(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "wallet", ref_id: pkg.id, origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setBusyId(null);
    }
  };

  const buyCustom = async () => {
    const n = Math.max(1, Math.min(50, Number(customCredits) || 1));
    setBusyId("custom");
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "wallet", ref_id: `custom_${n}`, origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setBusyId(null);
    }
  };

  const buyTalent = async (plan) => {
    setBusyId(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "talent_plan", ref_id: plan.id, origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setBusyId(null);
    }
  };

  return (
    <div data-testid="billing-page">
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="navy-bg rounded-xl p-7 text-white" data-testid="wallet-balance-card">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white/60">Recruitment Wallet balance</p>
            <Wallet className="w-5 h-5 text-[#F5A623]" />
          </div>
          <p className="font-heading font-extrabold text-4xl gold-text mt-3">{wallet?.balance ?? 0} credits</p>
          <p className="text-xs text-white/50 mt-2">
            {wallet?.purchased ?? 0} purchased · {wallet?.used ?? 0} used
          </p>
        </div>
        {packages.map((p) => (
          <div key={p.id} className="bg-white border border-border/60 rounded-xl p-7 card-hover" data-testid={`wallet-pack-${p.id}`}>
            <p className="font-heading font-bold text-2xl text-[#0B1B3D]">{p.credits} credits</p>
            <p className="text-sm text-muted-foreground mt-1">{inr(p.price)} + GST</p>
            <Button
              onClick={() => buy(p)}
              disabled={busyId === p.id}
              className="mt-5 rounded-full bg-[#0B1B3D] w-full"
              data-testid={`buy-${p.id}`}
            >
              {busyId === p.id ? "Redirecting…" : "Purchase Credits"}
            </Button>
          </div>
        ))}
        <div className="bg-white border-2 border-dashed border-[#2563EB]/40 rounded-xl p-7" data-testid="wallet-custom-card">
          <p className="font-heading font-bold text-2xl text-[#0B1B3D]">Custom credits</p>
          <p className="text-sm text-muted-foreground mt-1">Recharge from 1 credit · {inr(creditPrice)}/credit + GST</p>
          <div className="flex items-center gap-3 mt-4">
            <Input
              type="number" min={1} max={50} value={customCredits}
              onChange={(e) => setCustomCredits(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              className="w-24" data-testid="custom-credits-input"
            />
            <p className="text-sm font-semibold text-[#0B1B3D]" data-testid="custom-credits-total">
              {inr((Math.max(1, Math.min(50, Number(customCredits) || 1))) * creditPrice)}
            </p>
          </div>
          <Button
            onClick={buyCustom}
            disabled={busyId === "custom"}
            className="mt-4 rounded-full bg-[#2563EB] w-full"
            data-testid="buy-custom-credits-btn"
          >
            {busyId === "custom" ? "Redirecting…" : "Recharge"}
          </Button>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Talent Search add-on</h3>
        {talent?.bundled ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-4" data-testid="talent-bundled-banner">
            <p className="text-sm text-emerald-800 font-medium">
              Talent Search is included in your {talent.bundled_plan} subscription — unlimited profile access (fair use).
            </p>
          </div>
        ) : talent?.talent_plan ? (
          <div className="bg-white border border-border/60 rounded-xl p-5 mb-4" data-testid="talent-plan-card">
            <p className="font-heading font-semibold text-[#0B1B3D]">{talent.talent_plan.plan_name}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {talent.talent_plan.views_total - talent.talent_plan.views_used} of {talent.talent_plan.views_total} profile views remaining
            </p>
          </div>
        ) : null}
        <div className="grid sm:grid-cols-3 gap-5">
          {(talent?.available_plans || []).map((p) => (
            <div key={p.id} className="bg-white border border-border/60 rounded-xl p-6 card-hover" data-testid={`talent-plan-${p.id}`}>
              <p className="font-heading font-semibold text-[#0B1B3D]">{p.name}</p>
              <p className="text-sm text-muted-foreground mt-1">{p.views} profile views / month</p>
              <p className="font-heading font-bold text-xl text-[#0B1B3D] mt-3">{inr(p.price)}<span className="text-xs font-normal text-muted-foreground"> + GST</span></p>
              <Button onClick={() => buyTalent(p)} disabled={busyId === p.id} className="mt-4 rounded-full bg-[#0B1B3D] w-full" data-testid={`buy-talent-${p.id}`}>
                {busyId === p.id ? "Redirecting…" : "Purchase"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border/60 rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="data-export-card">
        <div>
          <p className="font-heading font-semibold text-sm text-[#0B1B3D]">Company data export</p>
          <p className="text-xs text-muted-foreground mt-1">Download your organization, jobs, invoices, wallet and employee records as JSON.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={async () => {
          try {
            const res = await api.get("/me/export", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "jobspring_company_data.json";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Company data export downloaded");
          } catch (e) { toast.error(errMsg(e)); }
        }} data-testid="export-company-btn">
          Export Company Data (JSON)
        </Button>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="mb-6">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet-history">Wallet History</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          {invoices.length === 0 ? (
            <EmptyState title="No invoices yet" description="Subscription, wallet and recruitment invoices will appear here." testId="invoices-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Invoice</th>
                    <th className="px-5 py-3.5 font-semibold">Description</th>
                    <th className="px-5 py-3.5 font-semibold">Base</th>
                    <th className="px-5 py-3.5 font-semibold">GST</th>
                    <th className="px-5 py-3.5 font-semibold">Total</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-border/40" data-testid={`invoice-${i.id}`}>
                      <td className="px-5 py-4 font-mono text-xs font-semibold">{i.invoice_no}</td>
                      <td className="px-5 py-4">{i.description}</td>
                      <td className="px-5 py-4">{inr(i.base)}</td>
                      <td className="px-5 py-4 text-xs">{inr(i.tax)} ({i.tax_pct}%)</td>
                      <td className="px-5 py-4 font-bold">{inr(i.total)}</td>
                      <td className="px-5 py-4"><StatusBadge status={i.status} /></td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(i.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="wallet">
          {walletTxs.length === 0 ? (
            <EmptyState title="No wallet transactions" description="Purchase a credit package to get started with prepaid recruitment." testId="wallet-empty" />
          ) : (
            <div className="space-y-3">
              {walletTxs.map((t) => (
                <div key={t.id} className="bg-white border border-border/60 rounded-xl p-5 flex items-center justify-between" data-testid={`wallet-tx-${t.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">{t.note}</p>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDate(t.created_at)}</p>
                  </div>
                  <span className={`font-heading font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-600"}`}>
                    {t.type === "credit" ? "+" : "-"}{t.credits} credits
                  </span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
